import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Checkbox,
  Text,
  Card,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { AppsRegular } from '@fluentui/react-icons';
import { OnboardingFormData } from '../../../hooks/useOnboardingForm';
import { apiClient, ListResponse } from '../../../api/client';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  intro: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  softwareItem: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalM,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  icon: {
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
  },
  content: {
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

interface SoftwareStepProps {
  formData: OnboardingFormData;
  updateFormData: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
}

interface Software {
  _id: string;
  name: string;
  description: string;
  category?: string;
  requiresLicense: boolean;
}

export function SoftwareStep({ formData, updateFormData }: SoftwareStepProps) {
  const styles = useStyles();
  const [software, setSoftware] = useState<Software[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<ListResponse<Software>>('/software')
      .then((response) => setSoftware(response.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = (softwareId: string) => {
    const currentSelection = formData.additionalSoftware;
    const newSelection = currentSelection.includes(softwareId)
      ? currentSelection.filter((id) => id !== softwareId)
      : [...currentSelection, softwareId];

    updateFormData('additionalSoftware', newSelection);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="medium" label="Loading software options..." />
      </div>
    );
  }

  if (software.length === 0) {
    return (
      <div className={styles.emptyState}>
        <AppsRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
        <Text>No additional software options available.</Text>
      </div>
    );
  }

  // Group software by category
  const groupedSoftware = software.reduce((acc, sw) => {
    const category = sw.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(sw);
    return acc;
  }, {} as Record<string, Software[]>);

  return (
    <div className={styles.container}>
      <Text className={styles.intro}>
        Select any additional software the new employee will need.
        Standard applications (Microsoft 365, Teams, etc.) are included automatically.
      </Text>

      {Object.entries(groupedSoftware).map(([category, items]) => (
        <div key={category}>
          <Text
            style={{
              fontSize: tokens.fontSizeBase200,
              fontWeight: tokens.fontWeightSemibold,
              color: tokens.colorNeutralForeground3,
              textTransform: 'uppercase',
              marginBottom: tokens.spacingVerticalS,
              display: 'block',
            }}
          >
            {category}
          </Text>
          {items.map((sw) => (
            <Card
              key={sw._id}
              className={styles.softwareItem}
              onClick={() => handleToggle(sw._id)}
            >
              <div className={styles.icon}>
                <AppsRegular />
              </div>
              <div className={styles.content}>
                <div className={styles.header}>
                  <Text className={styles.title}>{sw.name}</Text>
                  {sw.requiresLicense && (
                    <Badge appearance="outline" size="small">
                      License Required
                    </Badge>
                  )}
                </div>
                <Text className={styles.description}>{sw.description}</Text>
              </div>
              <Checkbox
                checked={formData.additionalSoftware.includes(sw._id)}
                onChange={() => handleToggle(sw._id)}
              />
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
}
